// @flow

import * as React from "react";
import { join, noop } from "lodash";

import SentryBoundary from "components/error_boundary";
import OptionSelectorList from "components/option_selector_list";
import SizeSelector from "components/size_selector";

import { get_photos_required, update_options } from "models/catalogue";
import { redirect } from "utils/common";
import { get } from "utils/ajax";

import type {
  LocalisedPrice,
  LogToServerFn,
  ProductDetails,
  Size
} from "types";

declare var gtag: LogToServerFn;

type Props = {
  product: string,
  details: ProductDetails
};

type State = {
  isUpdating: boolean,
  price: LocalisedPrice,
  unit_price: LocalisedPrice,
  selectedSize: string,
  selectedOptions: Array<string>
};

export default class Main extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      isUpdating: false,
      price: this.props.details.starts_from,
      unit_price: this.props.details.unit_price,
      selectedSize: this.props.details.default_size,
      selectedOptions: this.props.details.default_options
    };
  }

  _url_fragment = () => {
    return `product=${this.props.product}-${this.state.selectedSize}-${join(
      this.state.selectedOptions,
      ","
    )}`;
  };

  selectSize = (size: Size) => {
    this.setState({ selectedSize: size }, this.updatePrice);
    window.history.pushState(
      {},
      "url",
      `/products/${this.props.product}?size=${size}`
    );
    gtag("event", "product.sizes", { size: size });
  };

  selectOption = (prevOption: string, currentOption: string) => {
    this.setState(
      {
        selectedOptions: update_options(
          this.props.product,
          this.state.selectedOptions,
          prevOption,
          currentOption
        )
      },
      this.updatePrice
    );
    gtag("event", "product.options", { option: currentOption });
  };

  updatePrice = () => {
    this.setState({ isUpdating: true });
    get(
      `/api/v1/catalogue/price.json?${this._url_fragment()}`,
      {},
      (data: { price: LocalisedPrice, unit_price: LocalisedPrice }) => {
        this.setState({
          price: data.price,
          unit_price: data.unit_price,
          isUpdating: false
        });
      },
      noop,
      noop,
      "json",
      true
    );
  };

  handleBuyNow = () => {
    // If this is an accessory, like a wooden hanger, we will add this to the cart and
    // take the customer directly there.
    let photo_required = get_photos_required(
      this.props.product,
      this.state.selectedSize
    );
    // If we have a cart id, we  will use do_add_accessory to add accessories to the cart.
    // If we don't have a cart id, let do_add_accessory creates a new cart.
    let cartId = window.lastCartId || localStorage.getItem("guestCartId");
    if (photo_required === 0) {
      redirect(
        `/cart/do_add_accessory?cart=${cartId}&product=${this.props.product}&size=${this.state.selectedSize}`
      );
    } else {
      redirect(`/editor?${this._url_fragment()}`);
    }
  };

  componentDidMount = () => {
    // https://support.google.com/optimize/answer/7008840
    window.gtag("event", "optimize.activate");
  };

  render() {
    let priceKlass = "subtotal " + (this.state.isUpdating ? "loading" : "");
    let subtotal = this.state.price;
    if (
      this.props.details.show_unit_price &&
      this.state.unit_price !== this.state.price
    ) {
      subtotal = (
        <>
          {this.state.price}
          <small>{this.state.unit_price} each</small>
        </>
      );
    }

    return (
      <SentryBoundary>
        <div className="selector-group">
          <SizeSelector
            product={this.props.product}
            selectedSize={this.state.selectedSize}
            onSelect={this.selectSize}
          />
          <OptionSelectorList
            product={this.props.product}
            features={this.props.details.features}
            selectedOptions={this.state.selectedOptions}
            onSelect={this.selectOption}
          />
          <div className={priceKlass}>{subtotal}</div>
          {window.maintenanceMode ? (
            <>
              <button
                className="btn btn-control btn-control_highlighted btn-success"
                disabled>
                Start creating
              </button>
              <p className="text-warning" style={{ marginTop: "8px" }}>
                Our store is taking a little break. Ordering is paused for
                now. See you soon!
              </p>
            </>
          ) : (
            <a
              onClick={this.handleBuyNow}
              className="btn btn-control btn-control_highlighted btn-success">
              Start creating
            </a>
          )}
        </div>
      </SentryBoundary>
    );
  }
}

Main.displayName = "Main";
